<template>
  <div
    class="form-field-wrapper"
    :class="{
      compact: props.compact,
      horizontal: props.horizontal && type !== 'range' && type !== 'file'
    }"
  >
    <template v-if="type === 'range'">
      <div class="range-field">
        <div class="range-header">
          <label
            v-if="label"
            :for="id"
            class="range-label"
          >
            {{ label }}
          </label>
          <span v-if="rangeValueText" class="range-value">{{ rangeValueText }}</span>
        </div>

        <input
          :id="id"
          :value="modelValue ?? ''"
          class="range-input"
          type="range"
          :disabled="disabled"
          :required="required"
          :min="min"
          :max="max"
          :step="step"
          @blur="handleBlur"
          @input="handleRangeInput"
        />

        <div v-if="showRangeBounds" class="range-scale">
          <span>{{ formatSupportingValue(min) }}</span>
          <span>{{ formatSupportingValue(max) }}</span>
        </div>

        <p v-if="hint" class="range-hint">{{ hint }}</p>
        <p
          v-for="message in normalizedErrorMessages"
          :key="message"
          class="field-error"
        >
          {{ message }}
        </p>
      </div>
    </template>

    <template v-else-if="type === 'file'">
      <div class="file-field">
        <span
          v-if="label"
          class="file-label"
        >
          {{ label }}
        </span>

        <label
          :for="id"
          class="file-dropzone"
          :class="{ disabled: disabled }"
        >
          <v-icon v-if="icon" :icon="icon" size="32" class="file-icon" />
          <span class="file-title">{{ placeholder || 'Select a file' }}</span>
          <span v-if="hint" class="file-copy">{{ hint }}</span>
          <span v-if="fileName" class="file-selected">{{ fileName }}</span>
        </label>

        <input
          :id="id"
          class="file-input"
          type="file"
          :accept="accept"
          :disabled="disabled"
          :required="required"
          @blur="handleBlur"
          @change="handleFileChange"
        />

        <p
          v-for="message in normalizedErrorMessages"
          :key="message"
          class="field-error"
        >
          {{ message }}
        </p>
      </div>
    </template>

    <!-- Horizontal Layout Mode -->
    <template v-else-if="props.horizontal">
      <div class="field-label-group">
        <label
          v-if="label"
          :id="`${id}-label`"
          :for="id"
          class="field-label"
        >
          {{ label }}
        </label>
        <span v-if="hint && type === 'checkbox'" class="field-hint-sub">{{ hint }}</span>
      </div>

      <div class="field-input">
        <!-- Number Input -->
        <v-text-field
          v-if="type === 'number'"
          :id="id"
          :model-value="modelValue"
          :hint="hint"
          :error-messages="errorMessage"
          :disabled="disabled"
          :readonly="readonly"
          :required="required"
          :suffix="unit || suffix"
          :placeholder="placeholder"
          type="number"
          :step="step"
          :min="min"
          :max="max"
          :variant="props.fieldStyle || (props.compact ? 'filled' : 'outlined')"
          density="compact"
          hide-details="auto"
          @blur="handleBlur"
          @update:model-value="handleInput"
        ></v-text-field>

        <!-- Text Input -->
        <v-text-field
          v-else-if="type === 'text'"
          :id="id"
          :model-value="modelValue"
          :hint="hint"
          :error-messages="errorMessage"
          :disabled="disabled"
          :readonly="readonly"
          :required="required"
          :suffix="unit || suffix"
          :placeholder="placeholder"
          :variant="props.fieldStyle || (props.compact ? 'filled' : 'outlined')"
          density="compact"
          hide-details="auto"
          @blur="handleBlur"
          @update:model-value="handleInput"
        ></v-text-field>

        <!-- Password Input -->
        <v-text-field
          v-else-if="type === 'password'"
          :id="id"
          :model-value="modelValue"
          :hint="hint"
          :error-messages="errorMessage"
          :disabled="disabled"
          :readonly="readonly"
          :required="required"
          :placeholder="placeholder"
          :type="showPassword ? 'text' : 'password'"
          :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
          :variant="props.fieldStyle || (props.compact ? 'filled' : 'outlined')"
          density="compact"
          hide-details="auto"
          @click:append-inner="showPassword = !showPassword"
          @blur="handleBlur"
          @update:model-value="handleInput"
        ></v-text-field>

        <!-- Select/Dropdown -->
        <v-select
          v-else-if="type === 'select'"
          :id="id"
          :model-value="modelValue"
          :items="options"
          :hint="hint"
          :error-messages="errorMessage"
          :disabled="disabled"
          :readonly="readonly"
          :required="required"
          :placeholder="placeholder"
          :variant="props.fieldStyle || (props.compact ? 'filled' : 'outlined')"
          density="compact"
          hide-details="auto"
          @blur="handleBlur"
          @update:model-value="handleInput"
        ></v-select>

        <!-- Textarea -->
        <v-textarea
          v-else-if="type === 'textarea'"
          :id="id"
          :model-value="modelValue"
          :hint="hint"
          :error-messages="errorMessage"
          :disabled="disabled"
          :readonly="readonly"
          :required="required"
          :placeholder="placeholder"
          :variant="props.fieldStyle || (props.compact ? 'filled' : 'outlined')"
          density="compact"
          :rows="rows"
          hide-details="auto"
          @blur="handleBlur"
          @update:model-value="handleInput"
        ></v-textarea>

        <!-- Checkbox: hint stripped — shown in label area to prevent box shifting -->
        <v-checkbox
          v-else-if="type === 'checkbox'"
          :id="id"
          :model-value="modelValue"
          :error-messages="errorMessage"
          :disabled="disabled"
          :readonly="readonly"
          :required="required"
          density="compact"
          hide-details
          @blur="handleBlur"
          @update:model-value="handleInput"
        ></v-checkbox>
      </div>
    </template>

    <!-- Vertical Layout Mode (default) -->
    <template v-else>
      <!-- Number Input -->
      <v-text-field
        v-if="type === 'number'"
        :id="id"
        :model-value="modelValue"
        :label="label"
        :hint="hint"
        :error-messages="errorMessage"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :suffix="unit || suffix"
        :prefix="prefix"
        :placeholder="placeholder"
        type="number"
        :step="step"
        :min="min"
        :max="max"
        :variant="props.fieldStyle || (props.compact ? 'filled' : 'outlined')"
        density="compact"
        @blur="handleBlur"
        @update:model-value="handleInput"
      ></v-text-field>

      <!-- Text Input -->
      <v-text-field
        v-else-if="type === 'text'"
        :id="id"
        :model-value="modelValue"
        :label="label"
        :hint="hint"
        :error-messages="errorMessage"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :suffix="unit || suffix"
        :prefix="prefix"
        :placeholder="placeholder"
        :variant="props.fieldStyle || (props.compact ? 'filled' : 'outlined')"
        density="compact"
        @blur="handleBlur"
        @update:model-value="handleInput"
      ></v-text-field>

      <!-- Password Input -->
      <v-text-field
        v-else-if="type === 'password'"
        :id="id"
        :model-value="modelValue"
        :label="label"
        :hint="hint"
        :error-messages="errorMessage"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :placeholder="placeholder"
        :type="showPassword ? 'text' : 'password'"
        :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
        :variant="props.fieldStyle || (props.compact ? 'filled' : 'outlined')"
        density="compact"
        @click:append-inner="showPassword = !showPassword"
        @blur="handleBlur"
        @update:model-value="handleInput"
      ></v-text-field>

      <!-- Select/Dropdown -->
      <v-select
        v-else-if="type === 'select'"
        :id="id"
        :model-value="modelValue"
        :items="options"
        :label="label"
        :hint="hint"
        :error-messages="errorMessage"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :placeholder="placeholder"
        :variant="props.fieldStyle || (props.compact ? 'filled' : 'outlined')"
        density="compact"
        @blur="handleBlur"
        @update:model-value="handleInput"
      ></v-select>

      <!-- Textarea -->
      <v-textarea
        v-else-if="type === 'textarea'"
        :id="id"
        :model-value="modelValue"
        :label="label"
        :hint="hint"
        :error-messages="errorMessage"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :placeholder="placeholder"
        :variant="props.fieldStyle || (props.compact ? 'filled' : 'outlined')"
        density="compact"
        :rows="rows"
        @blur="handleBlur"
        @update:model-value="handleInput"
      ></v-textarea>

      <!-- Checkbox -->
      <v-checkbox
        v-else-if="type === 'checkbox'"
        :id="id"
        :model-value="modelValue"
        :label="props.horizontal ? undefined : label"
        :hint="hint"
        :error-messages="errorMessage"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        density="compact"
        @blur="handleBlur"
        @update:model-value="handleInput"
      ></v-checkbox>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

type FormFieldValue = string | number | boolean | null

interface SelectOption {
  title: string
  value: string | number
}

interface Props {
  id?: string
  modelValue: FormFieldValue
  type: 'text' | 'password' | 'number' | 'select' | 'textarea' | 'checkbox' | 'range' | 'file'
  label: string
  hint?: string
  errorMessage?: string | string[]
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  unit?: string // Engineering unit to display as suffix
  suffix?: string
  prefix?: string
  placeholder?: string
  compact?: boolean // Use filled variant with reduced spacing for section layouts
  horizontal?: boolean // Horizontal layout: label left, input center, unit right
  fieldStyle?: 'outlined' | 'filled' | 'underlined' | 'solo' | 'solo-inverted' | 'solo-filled' | 'plain' // Vuetify field variant
  // Number specific props
  step?: number | string
  min?: number | string
  max?: number | string
  // File specific props
  accept?: string
  icon?: string
  // Select specific props
  options?: SelectOption[]
  // Textarea specific props
  rows?: number
}

interface Emits {
  (e: 'update:modelValue', value: FormFieldValue): void
  (e: 'update:model-value', value: FormFieldValue): void
  (e: 'file-change', file: File | null): void
  (e: 'blur', event: Event): void
}

const props = withDefaults(defineProps<Props>(), {
  id: undefined,
  hint: '',
  errorMessage: '',
  disabled: false,
  readonly: false,
  required: false,
  suffix: '',
  prefix: '',
  placeholder: '',
  compact: false,
  horizontal: false,
  fieldStyle: 'outlined',
  step: 'any',
  rows: 3,
  accept: '',
  icon: '',
  options: () => []
})

const emit = defineEmits<Emits>()
const showPassword = ref(false)

const normalizedErrorMessages = computed(() =>
  Array.isArray(props.errorMessage)
    ? props.errorMessage.filter((message): message is string => Boolean(message))
    : props.errorMessage
      ? [props.errorMessage]
      : []
)

const showRangeBounds = computed(() => props.min !== undefined && props.max !== undefined)

const formatSupportingValue = (value: number | string | undefined): string => {
  if (value === undefined || value === '') {
    return ''
  }
  return props.unit || props.suffix ? `${value} ${props.unit || props.suffix}` : String(value)
}

const rangeValueText = computed(() => {
  if (props.modelValue === null || props.modelValue === '') {
    return ''
  }
  return formatSupportingValue(String(props.modelValue))
})

const fileName = computed(() => {
  return typeof props.modelValue === 'string' ? props.modelValue : ''
})

const handleInput = (value: FormFieldValue) => {
  // Input handler
  emit('update:modelValue', value)
  // Also emit kebab-case for lint/style compatibility in templates
  emit('update:model-value', value)
}

const handleRangeInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const nextValue = Number(target.value)
  handleInput(Number.isNaN(nextValue) ? target.value : nextValue)
}

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0] ?? null
  handleInput(file?.name ?? '')
  emit('file-change', file)
}

const handleBlur = (event: Event) => {
  emit('blur', event)
}
</script>

<style scoped>
.form-field-wrapper {
  margin-bottom: 12px;
}

.form-field-wrapper.compact {
  margin-bottom: 4px;
}

/* Vuetify lowers floating-label opacity by default; keep labels at AA contrast. */
.form-field-wrapper:not(.horizontal) :deep(.v-field-label) {
  color: var(--text-muted);
  opacity: 1;
}

/* Horizontal Layout Grid */
.form-field-wrapper.horizontal {
  display: grid;
  grid-template-columns: minmax(180px, auto) 1fr;
  gap: 1rem;
  align-items: center; /* Revert to center for better vertical alignment when no hint is present */
  margin-bottom: 8px;
}

.form-field-wrapper.horizontal .label-wrapper {
  display: flex;
  flex-direction: column;
  /* padding-top removed for center alignment */
}

.form-field-wrapper.horizontal .field-label {
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: var(--text-primary);
  margin: 0;
  white-space: normal;
  line-height: 1.4;
  word-break: break-word;
}

.form-field-wrapper.horizontal .field-hint {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  margin-top: 0.25rem;
  line-height: 1.3;
}

/* Mobile: Reduce label column width to allow text wrapping */
@media (max-width: 767px) {
  .form-field-wrapper.horizontal {
    grid-template-columns: minmax(100px, 0.8fr) 1fr;
    gap: 0.75rem;
  }
}

.form-field-wrapper.horizontal .field-input {
  display: flex;
  justify-content: flex-end;
  min-width: 0; /* Allow grid to shrink input if needed */
}

/* Compact, right-aligned numeric entry for all horizontal fields */
.form-field-wrapper.horizontal .field-input :deep(.v-field) {
  max-width: 190px;
  width: 100%;
  margin-left: auto;
}

.form-field-wrapper.horizontal .field-input :deep(.v-field__input) {
  text-align: right;
  font-variant-numeric: tabular-nums;
  padding-right: 0.25rem; /* 4px gap between typed value and unit suffix */
}

.form-field-wrapper.horizontal .field-input :deep(.v-field__suffix) {
  color: var(--text-muted);
}

/* Professional focus states - subtle background highlight to avoid clash with field labels */
:deep(.v-field:focus-within) {
  background: rgba(var(--accent-rgb), 0.04) !important;
  border-color: var(--accent) !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Reduced Motion: WCAG 2.4.13 — keep glow visible, remove the transition to it.
   Background + border-color are applied instantly (no fade). Focus ring stays compliant. */
@media (prefers-reduced-motion: reduce) {
  :deep(.v-field:focus-within) {
    transition: none !important;
  }
}

/* Filled variant focus states (for compact mode) */
:deep(.v-field--variant-filled:focus-within) {
  background: rgba(var(--accent-rgb), 0.06) !important;
}

/* Keep outlined border continuous: hide native number spinners inside Vuetify fields */
:deep(input[type="number"]) {
  appearance: textfield;       /* Standard */
  -moz-appearance: textfield;  /* Firefox */
}

:deep(input[type="number"]::-webkit-outer-spin-button),
:deep(input[type="number"]::-webkit-inner-spin-button) {
  -webkit-appearance: none; /* Chrome, Edge, Safari */
  margin: 0;
}
:deep(.v-field__prefix),
:deep(.v-field__suffix) {
  align-self: center;
}

/* Normalize input line-height within this field only to avoid subpixel shifts */
:deep(.v-field) .v-field__input {
  line-height: 1.5;
}

/* Label group: stacks label + hint sub-text on the left */
.form-field-wrapper.horizontal .field-label-group {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.field-hint-sub {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  margin-top: 0.25rem;
  line-height: 1.3;
}

.range-field,
.file-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.range-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.range-label,
.file-label {
  font-size: var(--font-size-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.range-value {
  font-family: var(--font-family-mono);
  font-size: var(--font-size-sm);
  font-weight: 700;
  color: var(--accent);
}

.range-input {
  width: 100%;
  height: 0.375rem;
  margin: 0;
  border-radius: var(--radius-pill);
  background: var(--bg-canvas);
  accent-color: var(--accent);
  cursor: pointer;
}

.range-scale {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-family: var(--font-family-mono);
  font-size: var(--font-size-2xs);
  color: var(--text-muted);
}

.range-hint,
.field-error {
  margin: 0;
  font-size: var(--font-size-xs);
  line-height: 1.4;
}

.range-hint {
  color: var(--text-muted);
}

.field-error {
  color: var(--state-error);
}

.file-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 10rem;
  border: 1px dashed var(--border-subtle);
  border-radius: var(--radius-xl);
  padding: 2rem 1.5rem;
  background: rgba(var(--accent-rgb), 0.02);
  color: var(--text-secondary);
  cursor: pointer;
  text-align: center;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.file-dropzone:hover,
.file-dropzone:focus-within {
  border-color: var(--accent);
  background: rgba(var(--accent-rgb), 0.05);
}

.file-dropzone.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.file-icon {
  color: var(--accent);
}

.file-title {
  color: var(--text-primary);
  font-weight: 700;
}

.file-copy {
  color: var(--text-body);
  font-size: var(--font-size-sm);
}

.file-selected {
  color: var(--accent);
  font-weight: 700;
  word-break: break-word;
}

.file-input {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  opacity: 0;
  pointer-events: none;
}

/* Checkbox specific: ensure right alignment without width jumps */
.form-field-wrapper.horizontal .field-input :deep(.v-checkbox) {
  display: flex;
  justify-content: flex-end;
  margin: 0;
  width: auto;
}

.form-field-wrapper.horizontal .field-input :deep(.v-selection-control) {
  justify-content: flex-end;
  width: auto;
}

/* Scale checkbox icon to match input pane font size */
:deep(.v-checkbox .v-selection-control__input .v-icon) {
  font-size: var(--font-size-md);
}

:deep(.v-checkbox .v-selection-control) {
  min-height: unset;
}
</style>
